import { useState, Fragment } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'
import { businessToday, addDays } from '../lib/dates'
import { Edit2, Plus, X, ChevronLeft, ChevronRight, Loader2, StopCircle, Sliders, RotateCcw } from 'lucide-react'
import {
  getStorageRates, updateStorageRates,
  getStorageEntries, createStorageEntry, updateStorageEntry,
  adjustStorageEntry, deleteStorageEntry, getStoragePreview,
} from '../lib/api'
import { useToast } from './Toast'

// ─── Constants ────────────────────────────────────────────────────────────────

const STORAGE_TYPES = [
  { key: 'controlled_temp_pallet', label: 'CT Pallet',       color: 'blue'   },
  { key: 'ambient_pallet',         label: 'Amb Pallet',      color: 'green'  },
  { key: 'controlled_temp_bin',    label: 'CT Bin',          color: 'purple' },
  { key: 'ambient_bin',            label: 'Ambient Bin',     color: 'amber'  },
]

const RATE_FIELD = {
  controlled_temp_pallet: 'controlled_temp_pallet_rate',
  ambient_pallet:         'ambient_pallet_rate',
  controlled_temp_bin:    'controlled_temp_bin_rate',
  ambient_bin:            'ambient_bin_rate',
}

const COLOR_CLASSES = {
  blue:   { bg: 'bg-blue-50',   text: 'text-blue-700',   badge: 'bg-blue-100 text-blue-700'   },
  green:  { bg: 'bg-emerald-50',text: 'text-emerald-700',badge: 'bg-emerald-100 text-emerald-700'},
  purple: { bg: 'bg-purple-50', text: 'text-purple-700', badge: 'bg-purple-100 text-purple-700'},
  amber:  { bg: 'bg-amber-50',  text: 'text-amber-700',  badge: 'bg-amber-100 text-amber-700'  },
}

function fmt(v) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(v ?? 0)
}

function today() {
  // Business date, not UTC. started_on is the ANCHOR for this client's rolling
  // 7-day storage blocks (backend/services/storage.py:155-160), so a one-day
  // error on a storage type's first entry re-phases every block that follows.
  return businessToday()
}

// ─── Storage Rates Modal ──────────────────────────────────────────────────────

function StorageRatesModal({ clientId, rates, onClose, onSaved }) {
  const [form, setForm] = useState({
    controlled_temp_pallet_rate: rates?.controlled_temp_pallet_rate ?? '',
    ambient_pallet_rate:         rates?.ambient_pallet_rate         ?? '',
    controlled_temp_bin_rate:    rates?.controlled_temp_bin_rate    ?? '',
    ambient_bin_rate:            rates?.ambient_bin_rate            ?? '',
  })
  const { addToast } = useToast()
  const mutation = useMutation({
    mutationFn: (data) => updateStorageRates(clientId, data),
    onSuccess: () => { onSaved(); onClose() },
    onError: () => addToast({ type: 'error', message: 'Failed to save storage rates.' }),
  })

  function set(field, val) {
    setForm(p => ({ ...p, [field]: val }))
  }

  function handleSubmit(e) {
    e.preventDefault()
    mutation.mutate({
      controlled_temp_pallet_rate: Number(form.controlled_temp_pallet_rate) || 0,
      ambient_pallet_rate:         Number(form.ambient_pallet_rate)         || 0,
      controlled_temp_bin_rate:    Number(form.controlled_temp_bin_rate)    || 0,
      ambient_bin_rate:            Number(form.ambient_bin_rate)            || 0,
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200">
          <h3 className="font-semibold text-slate-800">Storage Rates</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-5 space-y-4">
            <div className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-3 items-center">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Rate / unit / wk</div>
              {STORAGE_TYPES.map(st => (
                <Fragment key={st.key}>
                  <span className={`text-sm font-medium ${COLOR_CLASSES[st.color].text}`}>{st.label}</span>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                    <input
                      type="number" min="0" step="0.01"
                      value={form[RATE_FIELD[st.key]]}
                      onChange={e => set(RATE_FIELD[st.key], e.target.value)}
                      className="input pl-6 text-sm w-28"
                      placeholder="0.00"
                    />
                  </div>
                </Fragment>
              ))}
            </div>
            <p className="text-xs text-slate-400 border-t border-slate-100 pt-3">
              Billing weeks roll from the date storage first arrived. Each type is billed independently.
              If the last week of a period spills into the next month, those days are still billed in the current period.
            </p>
          </div>
          <div className="px-6 py-4 border-t border-slate-200 flex gap-3 justify-end">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={mutation.isPending} className="btn-primary">
              {mutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Edit2 size={14} />}
              {mutation.isPending ? 'Saving…' : 'Save Rates'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Add / Edit Storage Entry Modal ──────────────────────────────────────────

function StorageEntryModal({ clientId, entry = null, onClose, onSaved }) {
  const isEdit = !!entry
  const [form, setForm] = useState({
    storage_type: entry?.storage_type ?? 'controlled_temp_pallet',
    quantity:     entry?.quantity     ?? 1,
    notes:        entry?.notes        ?? '',
    started_on:   entry?.started_on   ?? today(),
    ended_on:     entry?.ended_on     ?? '',
  })
  const { addToast } = useToast()

  const mutation = useMutation({
    mutationFn: (data) => isEdit
      ? updateStorageEntry(entry.id, data)
      : createStorageEntry({ ...data, client_id: clientId }),
    onSuccess: () => { onSaved(); onClose() },
    onError: () => addToast({ type: 'error', message: 'Failed to save storage entry.' }),
  })

  function set(field, val) { setForm(p => ({ ...p, [field]: val })) }

  function handleSubmit(e) {
    e.preventDefault()
    mutation.mutate({
      storage_type: form.storage_type,
      quantity:     Number(form.quantity),
      notes:        form.notes || null,
      started_on:   form.started_on,
      ended_on:     form.ended_on || null,
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200">
          <h3 className="font-semibold text-slate-800">{isEdit ? 'Edit Storage Entry' : 'Add Storage'}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-5 space-y-4">
            <div>
              <label className="label">Storage Type</label>
              <select
                value={form.storage_type}
                onChange={e => set('storage_type', e.target.value)}
                className="input text-sm"
              >
                {STORAGE_TYPES.map(st => (
                  <option key={st.key} value={st.key}>{st.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Quantity</label>
              <input
                type="number" min="1"
                value={form.quantity}
                onChange={e => set('quantity', e.target.value)}
                className="input text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Start Date</label>
                <input type="date" value={form.started_on}
                  onChange={e => set('started_on', e.target.value)} className="input text-sm" />
              </div>
              <div>
                <label className="label">End Date <span className="text-slate-400">(optional)</span></label>
                <div className="flex gap-1">
                  <input type="date" value={form.ended_on || ''}
                    onChange={e => set('ended_on', e.target.value)} className="input text-sm flex-1" />
                  {form.ended_on && (
                    <button type="button" onClick={() => set('ended_on', '')}
                      title="Clear end date (re-open storage)"
                      className="px-2 py-1 text-xs text-slate-500 hover:text-red-600 hover:bg-red-50 rounded border border-slate-200">
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div>
              <label className="label">Notes <span className="text-slate-400">(optional)</span></label>
              <input type="text" value={form.notes}
                onChange={e => set('notes', e.target.value)}
                className="input text-sm" placeholder="e.g. SKU batch, PO reference" />
            </div>
          </div>
          <div className="px-6 py-4 border-t border-slate-200 flex gap-3 justify-end">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={mutation.isPending} className="btn-primary">
              {mutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              {mutation.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Storage'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Adjust Quantity Modal ────────────────────────────────────────────────────

function AdjustModal({ entry, onClose, onSaved }) {
  const [qty, setQty] = useState(entry.quantity)
  // Earliest valid effective_from = day after the entry started (backend rejects ≤ started_on).
  const startedIso = String(entry.started_on || '').slice(0, 10)
  // NB: this used to parse at LOCAL midnight and serialise back through UTC,
  // which cancelled its own +1 day in any UTC+ zone — minFromDate came back
  // equal to startedIso, so the input's `min` allowed a value the guard below
  // (`from > startedIso`) and the backend both rejected. addDays does the
  // arithmetic entirely in UTC, so it cannot pick up an offset.
  const minFromDate = startedIso ? addDays(startedIso, 1) : today()
  const defaultFrom = today() > minFromDate ? today() : minFromDate
  const [from, setFrom] = useState(defaultFrom)
  const { addToast } = useToast()
  const st = STORAGE_TYPES.find(t => t.key === entry.storage_type)

  const fromIsValid = !!from && from > startedIso
  const qtyIsValid  = Number(qty) >= 1 && Number(qty) !== Number(entry.quantity)

  const mutation = useMutation({
    mutationFn: () => adjustStorageEntry(entry.id, { new_quantity: Number(qty), effective_from: from }),
    onSuccess: () => { onSaved(); onClose() },
    onError: (err) => addToast({
      type: 'error',
      message: err.response?.data?.detail || err.message || 'Failed to adjust quantity.',
    }),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200">
          <div>
            <h3 className="font-semibold text-slate-800">Adjust Quantity</h3>
            <p className="text-xs text-slate-400 mt-0.5">{st?.label}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="label">Current Qty</label>
              <div className="input text-sm text-slate-500 bg-slate-50">{entry.quantity}</div>
            </div>
            <div className="flex-1">
              <label className="label">New Qty</label>
              <div className="flex items-center gap-1">
                <button type="button"
                  onClick={() => setQty(q => Math.max(1, Number(q) - 1))}
                  className="w-8 h-8 rounded border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50">−</button>
                <input type="number" min="1" value={qty}
                  onChange={e => setQty(e.target.value)}
                  className="input text-sm text-center w-16" />
                <button type="button"
                  onClick={() => setQty(q => Number(q) + 1)}
                  className="w-8 h-8 rounded border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50">+</button>
              </div>
            </div>
          </div>
          <div>
            <label className="label">Effective From</label>
            <input
              type="date"
              value={from}
              onChange={e => setFrom(e.target.value)}
              min={minFromDate}
              className="input text-sm"
            />
            <p className="text-xs text-slate-400 mt-1">
              Current entry will be closed the day before. Billed as full week for any week with active storage.
            </p>
            {!fromIsValid && from && (
              <p className="text-xs text-red-600 mt-1">
                Must be after the current entry's start date ({startedIso || '—'}).
              </p>
            )}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !fromIsValid || !qtyIsValid}
            className="btn-primary disabled:opacity-50"
          >
            {mutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Sliders size={14} />}
            {mutation.isPending ? 'Adjusting…' : 'Adjust'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Stop Storage Modal ───────────────────────────────────────────────────────

function StopModal({ entry, onClose, onSaved }) {
  const [endDate, setEndDate] = useState(today())
  const { addToast } = useToast()
  const st = STORAGE_TYPES.find(t => t.key === entry.storage_type)

  const mutation = useMutation({
    mutationFn: () => updateStorageEntry(entry.id, { ended_on: endDate }),
    onSuccess: () => { onSaved(); onClose() },
    onError: () => addToast({ type: 'error', message: 'Failed to stop storage.' }),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200">
          <div>
            <h3 className="font-semibold text-slate-800">Stop Storage</h3>
            <p className="text-xs text-slate-400 mt-0.5">{st?.label} — {entry.quantity} unit{entry.quantity > 1 ? 's' : ''}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <div className="px-6 py-5">
          <label className="label">Last Day in Storage</label>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="input text-sm" />
          <p className="text-xs text-slate-400 mt-1">The week containing this date is charged in full.</p>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={() => mutation.mutate()} disabled={mutation.isPending}
            className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-4 py-2 rounded-lg flex items-center gap-2 transition-colors">
            {mutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <StopCircle size={14} />}
            {mutation.isPending ? 'Stopping…' : 'Stop Storage'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Billing Calendar ─────────────────────────────────────────────────────────

function StorageCalendar({ preview, isLoading }) {
  if (isLoading) {
    return <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="skeleton h-24 rounded-xl" />)}</div>
  }
  if (!preview?.types) return null

  const activeTypes = preview.types.filter(tp => tp.type_total > 0 || tp.weeks.some(w => w.quantity > 0))

  if (!activeTypes.length) {
    return <div className="text-center py-8 text-slate-400 text-sm">No active storage entries for this period.</div>
  }

  return (
    <div className="space-y-5">
      {activeTypes.map(tp => {
        const st = STORAGE_TYPES.find(s => s.key === tp.storage_type)
        if (!st) return null
        return (
          <div key={tp.storage_type}>
            <div className="flex items-center justify-between mb-2">
              <div>
                <span className={`text-sm font-semibold ${COLOR_CLASSES[st.color].text}`}>{st.label}</span>
                {tp.anchor_date && (
                  <span className="ml-2 text-xs text-slate-400">
                    anchored {format(parseISO(tp.anchor_date), 'd MMM yyyy')}
                  </span>
                )}
              </div>
              <span className="text-xs text-slate-400">
                {format(parseISO(tp.window_start), 'd MMM')} – {format(parseISO(tp.window_end), 'd MMM yyyy')}
              </span>
            </div>
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Week</th>
                    <th className="px-4 py-2 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Qty</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {tp.weeks.map(wk => (
                    <tr key={wk.week_number} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-slate-700">Wk {wk.week_number}</div>
                        <div className="text-xs text-slate-400">
                          {format(parseISO(wk.week_start), 'd MMM')} – {format(parseISO(wk.week_end), 'd MMM')}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        {wk.quantity > 0
                          ? <span className={`font-medium ${COLOR_CLASSES[st.color].text}`}>{wk.quantity}</span>
                          : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right font-medium text-slate-700">
                        {wk.cost > 0 ? fmt(wk.cost) : <span className="text-slate-300">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 border-t-2 border-slate-200">
                    <td colSpan={2} className="px-4 py-2.5 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Type Total</td>
                    <td className="px-4 py-2.5 text-right font-bold text-slate-900">{fmt(tp.type_total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )
      })}
      <div className="flex justify-end pt-2 border-t-2 border-slate-200">
        <span className="text-sm font-bold text-slate-900">Period Total: {fmt(preview.grand_total)}</span>
      </div>
    </div>
  )
}

// ─── Active Entries Table ─────────────────────────────────────────────────────

function EntriesTable({ entries, isLoading, onAdjust, onStop, onEdit, onDelete, onReopen }) {
  if (isLoading) return <div className="skeleton h-24 rounded-xl" />
  if (!entries?.length) {
    return (
      <div className="text-center py-8 text-slate-400 text-sm">
        No storage entries yet. Click "Add Storage" to get started.
      </div>
    )
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
            <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Qty</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Started</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Ended</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Notes</th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {entries.map(entry => {
            const st = STORAGE_TYPES.find(t => t.key === entry.storage_type)
            const isActive = !entry.ended_on
            return (
              <tr key={entry.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${COLOR_CLASSES[st?.color ?? 'blue'].badge}`}>
                    {st?.label ?? entry.storage_type}
                  </span>
                </td>
                <td className="px-4 py-3 text-center font-medium text-slate-700">{entry.quantity}</td>
                <td className="px-4 py-3 text-slate-600">{format(parseISO(entry.started_on), 'd MMM yyyy')}</td>
                <td className="px-4 py-3">
                  {entry.ended_on
                    ? <span className="text-slate-500">{format(parseISO(entry.ended_on), 'd MMM yyyy')}</span>
                    : <span className="text-emerald-600 font-medium text-xs">Active</span>}
                </td>
                <td className="px-4 py-3 text-slate-500 text-xs">{entry.notes || '—'}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 justify-end">
                    {isActive ? (
                      <>
                        <button onClick={() => onAdjust(entry)} title="Adjust quantity"
                          className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                          <Sliders size={14} />
                        </button>
                        <button onClick={() => onStop(entry)} title="Stop storage"
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                          <StopCircle size={14} />
                        </button>
                      </>
                    ) : (
                      <button onClick={() => onReopen(entry)} title="Reopen (clear end date)"
                        className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors">
                        <RotateCcw size={14} />
                      </button>
                    )}
                    <button onClick={() => onEdit(entry)} title="Edit"
                      className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors">
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => onDelete(entry)} title="Delete"
                      className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                      <X size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ─── Main StorageTab ──────────────────────────────────────────────────────────

export default function StorageTab({ clientId }) {
  const qc = useQueryClient()
  const { addToast } = useToast()

  // Period navigation
  const now = new Date()
  const [period, setPeriod] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 })

  // Modal state
  const [showRates,  setShowRates]  = useState(false)
  const [showAdd,    setShowAdd]    = useState(false)
  const [editEntry,  setEditEntry]  = useState(null)
  const [adjustEntry,setAdjustEntry]= useState(null)
  const [stopEntry,  setStopEntry]  = useState(null)

  // Queries
  const { data: rates, isLoading: ratesLoading } = useQuery({
    queryKey: ['storage', 'rates', clientId],
    queryFn: () => getStorageRates(clientId),
    enabled: !!clientId,
  })

  const { data: entries, isLoading: entriesLoading } = useQuery({
    queryKey: ['storage', 'entries', clientId],
    queryFn: () => getStorageEntries(clientId),
    enabled: !!clientId,
  })

  const { data: preview, isLoading: previewLoading } = useQuery({
    queryKey: ['storage', 'preview', clientId, period.year, period.month],
    queryFn: () => getStoragePreview(clientId, period.year, period.month),
    enabled: !!clientId,
  })

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['storage', 'entries', clientId] })
    qc.invalidateQueries({ queryKey: ['storage', 'preview', clientId] })
    qc.invalidateQueries({ queryKey: ['admin', 'clients'] })
  }

  const deleteMutation = useMutation({
    mutationFn: (entry) => deleteStorageEntry(entry.id),
    onSuccess: () => { invalidate(); addToast({ type: 'success', message: 'Entry deleted.' }) },
    onError:   () => addToast({ type: 'error', message: 'Failed to delete entry.' }),
  })

  function handleDelete(entry) {
    if (window.confirm(`Delete this ${STORAGE_TYPES.find(t=>t.key===entry.storage_type)?.label} entry?`)) {
      deleteMutation.mutate(entry)
    }
  }

  // One-click re-open: clears ended_on. Relies on backend honoring an
  // explicit-null update (model_fields_set semantics in update_storage_entry).
  const reopenMutation = useMutation({
    mutationFn: (entry) => updateStorageEntry(entry.id, { ended_on: null }),
    onSuccess: () => { invalidate(); addToast({ type: 'success', message: 'Storage re-opened.' }) },
    onError:   () => addToast({ type: 'error', message: 'Failed to re-open storage.' }),
  })

  function handleReopen(entry) {
    if (window.confirm('Re-open this storage entry? The end date will be cleared and it will start billing again from now.')) {
      reopenMutation.mutate(entry)
    }
  }

  function prevMonth() {
    setPeriod(p => p.month === 1
      ? { year: p.year - 1, month: 12 }
      : { year: p.year, month: p.month - 1 })
  }
  function nextMonth() {
    setPeriod(p => p.month === 12
      ? { year: p.year + 1, month: 1 }
      : { year: p.year, month: p.month + 1 })
  }

  const monthLabel = new Date(period.year, period.month - 1).toLocaleString('en-AU', { month: 'long', year: 'numeric' })

  return (
    <div className="space-y-5">

      {/* Rates panel */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700">Storage Rates</h3>
          <button onClick={() => setShowRates(true)} className="btn-secondary text-xs py-1 px-3">
            <Edit2 size={12} /> Edit Rates
          </button>
        </div>
        {ratesLoading ? (
          <div className="skeleton h-8 rounded" />
        ) : rates ? (
          <div className="space-y-2">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {STORAGE_TYPES.map(st => (
                <div key={st.key} className={`rounded-lg px-3 py-2 ${COLOR_CLASSES[st.color].bg}`}>
                  <div className={`text-xs font-semibold ${COLOR_CLASSES[st.color].text} mb-1`}>{st.label}</div>
                  <div className="text-sm font-bold text-slate-800">{fmt(rates[RATE_FIELD[st.key]])}<span className="text-xs font-normal text-slate-400"> /wk</span></div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      {/* Calendar */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-700">Billing Calendar</h3>
          <div className="flex items-center gap-2">
            <button onClick={prevMonth} className="p-1.5 rounded hover:bg-slate-100 text-slate-500 transition-colors">
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-medium text-slate-700 w-36 text-center">{monthLabel}</span>
            <button onClick={nextMonth} className="p-1.5 rounded hover:bg-slate-100 text-slate-500 transition-colors">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
        <StorageCalendar preview={preview} isLoading={previewLoading} />
      </div>

      {/* Entries */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-700">Storage Entries</h3>
          <button onClick={() => setShowAdd(true)} className="btn-primary text-xs py-1.5 px-3">
            <Plus size={13} /> Add Storage
          </button>
        </div>
        <EntriesTable
          entries={entries}
          isLoading={entriesLoading}
          onAdjust={setAdjustEntry}
          onStop={setStopEntry}
          onEdit={setEditEntry}
          onDelete={handleDelete}
          onReopen={handleReopen}
        />
      </div>

      {/* Modals */}
      {showRates && (
        <StorageRatesModal
          clientId={clientId}
          rates={rates}
          onClose={() => setShowRates(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['storage', 'rates', clientId] })
            qc.invalidateQueries({ queryKey: ['storage', 'preview', clientId] })
            qc.invalidateQueries({ queryKey: ['admin', 'clients'] })
            addToast({ type: 'success', message: 'Storage rates saved.' })
          }}
        />
      )}
      {showAdd && (
        <StorageEntryModal
          clientId={clientId}
          onClose={() => setShowAdd(false)}
          onSaved={() => { invalidate(); addToast({ type: 'success', message: 'Storage entry added.' }) }}
        />
      )}
      {editEntry && (
        <StorageEntryModal
          clientId={clientId}
          entry={editEntry}
          onClose={() => setEditEntry(null)}
          onSaved={() => { invalidate(); setEditEntry(null); addToast({ type: 'success', message: 'Entry updated.' }) }}
        />
      )}
      {adjustEntry && (
        <AdjustModal
          entry={adjustEntry}
          onClose={() => setAdjustEntry(null)}
          onSaved={() => { invalidate(); setAdjustEntry(null); addToast({ type: 'success', message: 'Quantity adjusted.' }) }}
        />
      )}
      {stopEntry && (
        <StopModal
          entry={stopEntry}
          onClose={() => setStopEntry(null)}
          onSaved={() => { invalidate(); setStopEntry(null); addToast({ type: 'success', message: 'Storage stopped.' }) }}
        />
      )}
    </div>
  )
}
